import { Paper, Stack, Group, Text, Badge, Tooltip } from '@mantine/core'
import { IconUserCheck, IconUserOff, IconUserStar } from '@tabler/icons-react'

/**
 * Roster display. Backend scopes the member shape:
 *  - facilitator view: full_name + email present alongside display_name
 *  - member view: display_name only
 *
 * Inactive members (those who lost Group access) render dimmed with a badge
 * but stay in the roster — by design we keep them and their history.
 */
export function TeamRosterCard({ members = [], facilitatorView = false }) {
  if (members.length === 0) {
    return (
      <Paper p="lg" className="glass">
        <Text size="sm" c="dimmed">No members yet — share the invite link to get started.</Text>
      </Paper>
    )
  }

  return (
    <Paper p="lg" className="glass">
      <Stack gap="sm">
        <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" tt="uppercase" lts={1}>
          Roster ({members.length})
        </Text>
        {members.map((m) => (
          <RosterRow key={m.id || m.user_id} member={m} facilitatorView={facilitatorView} />
        ))}
      </Stack>
    </Paper>
  )
}

function RosterRow({ member, facilitatorView }) {
  const inactive = member.status === 'inactive'
  const isFacilitator = !!member.is_facilitator

  return (
    <Group justify="space-between" align="center" wrap="nowrap" style={{ opacity: inactive ? 0.55 : 1 }}>
      <Group gap="sm" wrap="nowrap">
        {inactive ? <IconUserOff size={16} color="var(--mantine-color-gray-5)" /> : <IconUserCheck size={16} color="var(--mantine-color-cyan-4)" />}
        <Stack gap={0}>
          <Group gap={6}>
            <Text size="sm" fw={600}>
              {member.display_name || 'Unnamed'}
            </Text>
            {isFacilitator && (
              <Tooltip label="Facilitator">
                <IconUserStar size={12} color="var(--mantine-color-cyan-4)" />
              </Tooltip>
            )}
          </Group>
          {facilitatorView && (member.full_name || member.email) && (
            <Text size="xs" c="dimmed">
              {member.full_name}{member.full_name && member.email ? ' — ' : ''}{member.email}
            </Text>
          )}
        </Stack>
      </Group>

      <Group gap="xs">
        {inactive && (
          <Badge size="xs" color="gray" variant="light">Inactive</Badge>
        )}
        {member.current_story_status && !inactive && (
          <Badge
            size="xs"
            color={member.current_story_status === 'completed' ? 'cyan' : 'gray'}
            variant="light"
          >
            {member.current_story_status === 'completed' ? 'Completed' : member.current_story_status === 'in_progress' ? 'In progress' : 'Not started'}
          </Badge>
        )}
      </Group>
    </Group>
  )
}
